import { createContext, useContext, useEffect, useState } from "react";

const STORE_KEY = "sp.lang";

/* Lightweight, offline static dictionary translator.
 * Zero network calls, ~5 KB gzipped, instant switching. */
const STRINGS = {
  en: {
    // navigation
    "nav.home": "Home", "nav.tickets": "Tickets", "nav.chat": "Chat", "nav.account": "Account", "nav.tasks": "Tasks",
    // common
    "common.cancel": "Cancel", "common.send": "Send", "common.save": "Save", "common.close": "Close",
    "common.continue": "Continue", "common.back": "Back", "common.search": "Search", "common.add": "Add",
    "common.delete": "Delete", "common.signin": "Sign in", "common.submit": "Submit",
    "common.seeAll": "See all", "common.loading": "Loading…", "common.today": "Today",
    "common.yesterday": "Yesterday", "common.urgent": "Urgent",
    // login
    "login.welcome": "Welcome", "login.to2026": "to 2026",
    "login.tagline": "One tap. One number. Everything that matters in your society — tasks, complaints, neighbours — finally in one calm place.",
    "login.mobile": "Mobile number", "login.phonePlaceholder": "10-digit number",
    "login.smsHint": "We'll send you a one-time code to confirm.",
    "login.demo": "Demo", "login.demoBody": "Use 8237573829 with OTP 1234 to sign in instantly.",
    "login.resident": "Resident", "login.staff": "Society Staff", "login.role": "Choose your role",
    "login.password": "Password", "login.changeNumber": "Change number", "login.enterCode": "Enter the 4-digit code",
    "login.sentTo": "Sent to", "login.verifyContinue": "Verify & continue", "login.resend": "Resend OTP",
    "login.almost": "Almost there", "login.tellUs": "Tell us about you", "login.yourName": "Your name",
    "login.selectFlat": "Select your flat", "login.wing": "Wing", "login.floor": "Floor", "login.flat": "Flat",
    "login.getStarted": "Get started", "login.terms": "By continuing you agree to our community rules · v0.1 · 2026",
    // home
    "home.morning": "Good morning", "home.afternoon": "Good afternoon", "home.evening": "Good evening",
    "home.flat": "Flat", "home.society": "Silver Altire",
    "home.pulse": "Society pulse", "home.clear": "All clear today.",
    "home.noUrgent": "No urgent issues at your flat.",
    "home.openCount": "open", "home.inProgressCount": "in progress",
    "home.quickRaise": "Raise a Ticket", "home.messageOffice": "Message Society Manager",
    "home.societyStaff": "Society staff", "home.viewAttendance": "view & attendance",
    "home.announcements": "Announcements", "home.latest": "latest",
    "home.recentTickets": "Your Recent Tickets", "home.noReport": "Nothing to report yet",
    "home.tapToRaise": "Tap to raise your first complaint or request.",
    // tickets
    "tickets.title": "Complaints", "tickets.yourTickets": "Your tickets", "tickets.allTickets": "All tickets",
    "tickets.searchResults": "Search results", "tickets.search": "Search by ticket ID, title or flat",
    "tickets.raise": "Raise", "tickets.raiseTicket": "Raise a ticket", "tickets.category": "Category",
    "tickets.titleField": "Title", "tickets.description": "Description",
    "tickets.titlePlaceholder": "e.g. Kitchen tap is leaking",
    "tickets.descPlaceholder": "Add a few details so the team knows what to expect.",
    "tickets.submit": "Submit ticket", "tickets.submitting": "Submitting…",
    "tickets.startWork": "Start work", "tickets.resolve": "Resolve",
    "tickets.created": "Ticket created", "tickets.saveId": "Save this ID — the office will reference it in updates.",
    "tickets.noMatch": "No tickets matched", "tickets.noTickets": "No tickets yet",
    "tickets.noMatchHint": "Try a partial TKT-id, title or flat code.",
    // chat
    "chat.title": "Chat", "chat.conversations": "Conversations", "chat.lounge": "Society Lounge",
    "chat.everyone": "Staff group thread", "chat.group": "GROUP", "chat.office": "Office threads",
    "chat.noOffice": "No office threads yet.", "chat.placeholder": "Type a message…",
    "chat.placeholderUrgent": "URGENT message — type here…",
    "chat.recordingVoice": "Recording voice note", "chat.stopSend": "Stop & Send",
    "chat.replyTo": "Replying to", "chat.markUrgent": "Mark urgent",
    "chat.photo": "Photo", "chat.file": "File",
    // account
    "account.title": "Account", "account.you": "You", "account.mobile": "Mobile", "account.loginCode": "Login code",
    "account.management": "Management", "account.signout": "Sign out", "account.language": "Language",
    "account.myFlat": "My Flat", "account.familyMembers": "Family members",
    "account.addMember": "Add member", "account.familyHint": "A flat can have many people — add family members so they show up across the app.",
    // staff
    "staff.security": "Security", "staff.housekeeping": "Housekeeping", "staff.supervisors": "Supervisors",
    "staff.members": "Members", "staff.allShifts": "All shifts", "staff.day": "Day shift", "staff.night": "Night shift",
    "staff.markPresent": "Mark", "staff.present": "Present", "staff.markingLockedToday": "Marking is enabled only for the current day. Earlier days are read-only.",
    // summary
    "summary.title": "Attendance summary", "summary.subtitle": "Supervisor-wise present % across security shifts and housekeeping.",
    // announcements
    "ann.allTitle": "All announcements", "ann.empty": "No announcements yet.",
  },
  hi: {
    "nav.home": "होम", "nav.tickets": "टिकट", "nav.chat": "चैट", "nav.account": "अकाउंट", "nav.tasks": "कार्य",
    "common.cancel": "रद्द करें", "common.send": "भेजें", "common.save": "सहेजें", "common.close": "बंद करें",
    "common.continue": "जारी रखें", "common.back": "वापस", "common.search": "खोजें", "common.add": "जोड़ें",
    "common.delete": "हटाएँ", "common.signin": "साइन इन", "common.submit": "सबमिट करें",
    "common.seeAll": "सभी देखें", "common.loading": "लोड हो रहा है…", "common.today": "आज",
    "common.yesterday": "कल", "common.urgent": "तत्काल",
    "login.welcome": "स्वागत", "login.to2026": "2026 में",
    "login.tagline": "एक टैप. एक नंबर. आपकी सोसायटी में जो ज़रूरी है — काम, शिकायतें, पड़ोसी — एक ही जगह.",
    "login.mobile": "मोबाइल नंबर", "login.phonePlaceholder": "10-अंकीय नंबर",
    "login.smsHint": "हम पुष्टि के लिए एक OTP भेजेंगे.",
    "login.demo": "डेमो", "login.demoBody": "तुरंत साइन इन के लिए 8237573829 और OTP 1234 का उपयोग करें.",
    "login.resident": "निवासी", "login.staff": "सोसायटी स्टाफ", "login.role": "अपनी भूमिका चुनें",
    "login.password": "पासवर्ड", "login.changeNumber": "नंबर बदलें", "login.enterCode": "4-अंकीय कोड दर्ज करें",
    "login.sentTo": "भेजा गया", "login.verifyContinue": "सत्यापित करें", "login.resend": "OTP फिर से भेजें",
    "login.almost": "लगभग पूरा", "login.tellUs": "अपने बारे में बताएँ", "login.yourName": "आपका नाम",
    "login.selectFlat": "अपना फ्लैट चुनें", "login.wing": "विंग", "login.floor": "मंज़िल", "login.flat": "फ्लैट",
    "login.getStarted": "शुरू करें", "login.terms": "जारी रखते हुए आप हमारे नियम स्वीकार करते हैं · v0.1 · 2026",
    "home.morning": "सुप्रभात", "home.afternoon": "शुभ दोपहर", "home.evening": "शुभ संध्या",
    "home.flat": "फ्लैट", "home.society": "सिल्वर अल्टायर",
    "home.pulse": "सोसायटी पल्स", "home.clear": "आज सब कुछ ठीक है.",
    "home.noUrgent": "आपके फ्लैट में कोई तत्काल समस्या नहीं.",
    "home.openCount": "खुले", "home.inProgressCount": "प्रगति पर",
    "home.quickRaise": "शिकायत दर्ज करें", "home.messageOffice": "ऑफिस को संदेश",
    "home.societyStaff": "सोसायटी स्टाफ", "home.viewAttendance": "देखें · उपस्थिति",
    "home.announcements": "घोषणाएँ", "home.latest": "नवीनतम",
    "home.recentTickets": "आपके हाल के टिकट", "home.noReport": "अभी कुछ रिपोर्ट नहीं",
    "home.tapToRaise": "अपनी पहली शिकायत दर्ज करने के लिए टैप करें.",
    "tickets.title": "शिकायतें", "tickets.yourTickets": "आपके टिकट", "tickets.allTickets": "सभी टिकट",
    "tickets.searchResults": "खोज परिणाम", "tickets.search": "टिकट ID, शीर्षक या फ्लैट से खोजें",
    "tickets.raise": "दर्ज करें", "tickets.raiseTicket": "टिकट दर्ज करें", "tickets.category": "श्रेणी",
    "tickets.titleField": "शीर्षक", "tickets.description": "विवरण",
    "tickets.titlePlaceholder": "जैसे: रसोई का नल टपक रहा है",
    "tickets.descPlaceholder": "टीम को बेहतर जानकारी देने के लिए विवरण जोड़ें.",
    "tickets.submit": "टिकट सबमिट करें", "tickets.submitting": "सबमिट हो रहा है…",
    "tickets.startWork": "कार्य शुरू करें", "tickets.resolve": "हल किया",
    "tickets.created": "टिकट बना", "tickets.saveId": "यह ID सहेजें — अपडेट्स में संदर्भ मिलेगा.",
    "tickets.noMatch": "कोई टिकट नहीं मिला", "tickets.noTickets": "अभी कोई टिकट नहीं",
    "tickets.noMatchHint": "आंशिक TKT-id, शीर्षक या फ्लैट कोड आज़माएँ.",
    "chat.title": "चैट", "chat.conversations": "बातचीत", "chat.lounge": "सोसायटी लाउंज",
    "chat.everyone": "स्टाफ समूह थ्रेड", "chat.group": "समूह", "chat.office": "ऑफिस थ्रेड्स",
    "chat.noOffice": "अभी कोई ऑफिस थ्रेड नहीं.", "chat.placeholder": "संदेश लिखें…",
    "chat.placeholderUrgent": "तत्काल संदेश यहाँ लिखें…",
    "chat.recordingVoice": "ऑडियो रिकॉर्ड हो रहा है", "chat.stopSend": "रोकें और भेजें",
    "chat.replyTo": "उत्तर:", "chat.markUrgent": "तत्काल चिह्नित करें",
    "chat.photo": "फोटो", "chat.file": "फ़ाइल",
    "account.title": "अकाउंट", "account.you": "आप", "account.mobile": "मोबाइल", "account.loginCode": "लॉगिन कोड",
    "account.management": "प्रबंधन", "account.signout": "साइन आउट", "account.language": "भाषा",
    "account.myFlat": "मेरा फ्लैट", "account.familyMembers": "परिवार सदस्य",
    "account.addMember": "सदस्य जोड़ें", "account.familyHint": "एक फ्लैट में कई सदस्य हो सकते हैं — परिवार सदस्य जोड़ें.",
    "staff.security": "सुरक्षा", "staff.housekeeping": "हाउसकीपिंग", "staff.supervisors": "सुपरवाइज़र",
    "staff.members": "सदस्य", "staff.allShifts": "सभी शिफ़्ट", "staff.day": "दिन शिफ़्ट", "staff.night": "रात शिफ़्ट",
    "staff.markPresent": "चिह्नित", "staff.present": "उपस्थित", "staff.markingLockedToday": "अंकन केवल आज के लिए. पुराने दिन रीड-ओनली हैं.",
    "summary.title": "उपस्थिति सारांश", "summary.subtitle": "सुरक्षा शिफ़्ट और हाउसकीपिंग में सुपरवाइज़र-वार उपस्थिति %.",
    "ann.allTitle": "सभी घोषणाएँ", "ann.empty": "अभी कोई घोषणा नहीं.",
  },
  mr: {
    "nav.home": "मुख्य", "nav.tickets": "तिकिटे", "nav.chat": "गप्पा", "nav.account": "खाते", "nav.tasks": "कामे",
    "common.cancel": "रद्द करा", "common.send": "पाठवा", "common.save": "जतन करा", "common.close": "बंद करा",
    "common.continue": "पुढे जा", "common.back": "मागे", "common.search": "शोधा", "common.add": "जोडा",
    "common.delete": "हटवा", "common.signin": "साइन इन", "common.submit": "सबमिट",
    "common.seeAll": "सर्व पहा", "common.loading": "लोड होत आहे…", "common.today": "आज",
    "common.yesterday": "काल", "common.urgent": "तातडीचे",
    "login.welcome": "स्वागत", "login.to2026": "२०२६ मध्ये",
    "login.tagline": "एक टॅप. एक क्रमांक. तुमच्या सोसायटीतल्या सर्व गोष्टी — कामे, तक्रारी, शेजारी — एका ठिकाणी.",
    "login.mobile": "मोबाईल क्रमांक", "login.phonePlaceholder": "१० अंकी क्रमांक",
    "login.smsHint": "आम्ही पुष्टीसाठी एक OTP पाठवू.",
    "login.demo": "डेमो", "login.demoBody": "लगेच साइन इन साठी 8237573829 आणि OTP 1234 वापरा.",
    "login.resident": "रहिवासी", "login.staff": "सोसायटी कर्मचारी", "login.role": "तुमची भूमिका निवडा",
    "login.password": "पासवर्ड", "login.changeNumber": "क्रमांक बदला", "login.enterCode": "४ अंकी कोड टाका",
    "login.sentTo": "पाठवले", "login.verifyContinue": "पडताळा आणि पुढे जा", "login.resend": "OTP पुन्हा पाठवा",
    "login.almost": "जवळजवळ झालं", "login.tellUs": "तुमच्याबद्दल सांगा", "login.yourName": "तुमचं नाव",
    "login.selectFlat": "तुमचा फ्लॅट निवडा", "login.wing": "विंग", "login.floor": "मजला", "login.flat": "फ्लॅट",
    "login.getStarted": "सुरू करा", "login.terms": "पुढे जाताना आपण नियम स्वीकारता · v0.1 · 2026",
    "home.morning": "सुप्रभात", "home.afternoon": "शुभ दुपार", "home.evening": "शुभ संध्याकाळ",
    "home.flat": "फ्लॅट", "home.society": "सिल्व्हर अल्टायर",
    "home.pulse": "सोसायटी पल्स", "home.clear": "आज सर्व ठीक.",
    "home.noUrgent": "तुमच्या फ्लॅटमध्ये कोणताही तातडीचा प्रश्न नाही.",
    "home.openCount": "उघडे", "home.inProgressCount": "सुरू",
    "home.quickRaise": "तक्रार नोंदवा", "home.messageOffice": "ऑफिसला संदेश",
    "home.societyStaff": "सोसायटी कर्मचारी", "home.viewAttendance": "पहा · उपस्थिती",
    "home.announcements": "घोषणा", "home.latest": "नवीनतम",
    "home.recentTickets": "तुमची अलीकडची तिकिटे", "home.noReport": "अहवालासाठी काही नाही",
    "home.tapToRaise": "पहिली तक्रार नोंदवण्यासाठी टॅप करा.",
    "tickets.title": "तक्रारी", "tickets.yourTickets": "तुमची तिकिटे", "tickets.allTickets": "सर्व तिकिटे",
    "tickets.searchResults": "शोध निकाल", "tickets.search": "तिकीट ID, शीर्षक किंवा फ्लॅटद्वारे शोधा",
    "tickets.raise": "नोंदवा", "tickets.raiseTicket": "तिकीट नोंदवा", "tickets.category": "श्रेणी",
    "tickets.titleField": "शीर्षक", "tickets.description": "वर्णन",
    "tickets.titlePlaceholder": "उदा: स्वयंपाकघरातला नळ गळतोय",
    "tickets.descPlaceholder": "टीमला अधिक माहितीसाठी तपशील जोडा.",
    "tickets.submit": "तिकीट सबमिट करा", "tickets.submitting": "सबमिट होत आहे…",
    "tickets.startWork": "काम सुरू करा", "tickets.resolve": "सोडवले",
    "tickets.created": "तिकीट तयार", "tickets.saveId": "हा ID जतन करा — अद्यतनांत संदर्भ दिला जाईल.",
    "tickets.noMatch": "एकही तिकीट मिळाले नाही", "tickets.noTickets": "अद्याप तिकिटे नाहीत",
    "tickets.noMatchHint": "आंशिक TKT-id, शीर्षक किंवा फ्लॅट कोड वापरून पहा.",
    "chat.title": "गप्पा", "chat.conversations": "संभाषणे", "chat.lounge": "सोसायटी लाउंज",
    "chat.everyone": "कर्मचारी समूह थ्रेड", "chat.group": "ग्रुप", "chat.office": "ऑफिस थ्रेड्स",
    "chat.noOffice": "अद्याप ऑफिस थ्रेड नाहीत.", "chat.placeholder": "संदेश लिहा…",
    "chat.placeholderUrgent": "तातडीचा संदेश इथे लिहा…",
    "chat.recordingVoice": "व्हॉइस नोट रेकॉर्ड होतंय", "chat.stopSend": "थांबवा आणि पाठवा",
    "chat.replyTo": "उत्तर:", "chat.markUrgent": "तातडीचे चिन्हांकित करा",
    "chat.photo": "फोटो", "chat.file": "फाइल",
    "account.title": "खाते", "account.you": "तुम्ही", "account.mobile": "मोबाईल", "account.loginCode": "लॉगिन कोड",
    "account.management": "व्यवस्थापन", "account.signout": "साइन आऊट", "account.language": "भाषा",
    "account.myFlat": "माझा फ्लॅट", "account.familyMembers": "कुटुंबीय सदस्य",
    "account.addMember": "सदस्य जोडा", "account.familyHint": "एका फ्लॅटमध्ये अनेक माणसे असू शकतात — कुटुंबीय जोडा.",
    "staff.security": "सुरक्षा", "staff.housekeeping": "हाउसकीपिंग", "staff.supervisors": "पर्यवेक्षक",
    "staff.members": "सदस्य", "staff.allShifts": "सर्व पाळ्या", "staff.day": "दिवस पाळी", "staff.night": "रात्र पाळी",
    "staff.markPresent": "मार्क", "staff.present": "उपस्थित", "staff.markingLockedToday": "मार्किंग फक्त आजसाठी सक्षम. पूर्वीचे दिवस फक्त-वाचनासाठी.",
    "summary.title": "उपस्थिती सारांश", "summary.subtitle": "सुरक्षा पाळ्या आणि हाउसकीपिंगवर पर्यवेक्षक-निहाय उपस्थिती %.",
    "ann.allTitle": "सर्व घोषणा", "ann.empty": "अद्याप घोषणा नाहीत.",
  },
};

const LangCtx = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(STORE_KEY) || "en");
  useEffect(() => {
    localStorage.setItem(STORE_KEY, lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
}

export function useI18n() { return useContext(LangCtx); }
export const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "mr", label: "मराठी" },
];
